import { useEffect, useRef, useState } from 'react';
import type { Call } from '@twilio/voice-sdk';

interface UseActiveSpeakerDetectionOptions {
	speechThreshold?: number;
	relativeThreshold?: number;
}

/**
 * Custom hook for detecting active speaker in a voice call
 * @param call - The active Twilio call object
 * @param options - Optional configuration for detection thresholds
 * @returns isCustomerSpeaking - Boolean indicating if the customer is currently speaking
 */
export const useActiveSpeakerDetection = (
	call: Call | null,
	options: UseActiveSpeakerDetectionOptions = {}
) => {
	const { speechThreshold = 10, relativeThreshold = 1.5 } = options;

	const [isCustomerSpeaking, setIsCustomerSpeaking] = useState(false);
	const audioContextRef = useRef<AudioContext | null>(null);
	const localAnalyserRef = useRef<AnalyserNode | null>(null);
	const remoteAnalyserRef = useRef<AnalyserNode | null>(null);
	const animationFrameRef = useRef<number | null>(null);

	useEffect(() => {
		if (!call) {
			return;
		}

		const startDetection = () => {
			try {
				const remoteStream = call.getRemoteStream();
				const localStream = call.getLocalStream();

				if (!remoteStream || !localStream) {
					console.warn(
						'[ActiveSpeaker] Missing streams for speaker detection'
					);
					return;
				}

				const audioContext = new AudioContext();
				audioContextRef.current = audioContext;

				// Create analyzer for remote stream (customer)
				const remoteAnalyser = audioContext.createAnalyser();
				remoteAnalyser.fftSize = 256;
				remoteAnalyser.smoothingTimeConstant = 0.8;
				const remoteSource =
					audioContext.createMediaStreamSource(remoteStream);
				remoteSource.connect(remoteAnalyser);
				remoteAnalyserRef.current = remoteAnalyser;

				// Create analyzer for local stream (user)
				const localAnalyser = audioContext.createAnalyser();
				localAnalyser.fftSize = 256;
				localAnalyser.smoothingTimeConstant = 0.8;
				const localSource =
					audioContext.createMediaStreamSource(localStream);
				localSource.connect(localAnalyser);
				localAnalyserRef.current = localAnalyser;

				// Calculate average audio level from frequency data
				const getAudioLevel = (analyser: AnalyserNode): number => {
					const dataArray = new Uint8Array(
						analyser.frequencyBinCount
					);
					analyser.getByteFrequencyData(dataArray);
					const sum = dataArray.reduce((acc, val) => acc + val, 0);
					return sum / dataArray.length;
				};

				// Detection loop
				const detectActiveSpeaker = () => {
					if (
						!localAnalyserRef.current ||
						!remoteAnalyserRef.current
					) {
						return;
					}

					const localLevel = getAudioLevel(localAnalyserRef.current);
					const remoteLevel = getAudioLevel(
						remoteAnalyserRef.current
					);

					// Customer is speaking if their audio level is above threshold
					// and significantly higher than local audio
					const customerSpeaking =
						remoteLevel > speechThreshold &&
						remoteLevel > localLevel * relativeThreshold;

					setIsCustomerSpeaking(customerSpeaking);

					animationFrameRef.current =
						requestAnimationFrame(detectActiveSpeaker);
				};

				detectActiveSpeaker();
			} catch (err) {
				console.error('[ActiveSpeaker] Failed to start detection', err);
			}
		};

		startDetection();

		// Cleanup function
		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
				animationFrameRef.current = null;
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
				audioContextRef.current = null;
			}
			setIsCustomerSpeaking(false);
			localAnalyserRef.current = null;
			remoteAnalyserRef.current = null;
		};
	}, [call, speechThreshold, relativeThreshold]);

	return isCustomerSpeaking;
};
